import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import CountdownTimer from "../components/CountdownTimer";
import { useCurrency } from "../context/CurrencyContext";
import Seo from "../components/Seo";
import { breadcrumbSchema } from "../lib/schema";
import VideoBadge from "../components/VideoBadge";

const TABS = [
  { key: "live", label: "Live" },
  { key: "upcoming", label: "Upcoming" },
  { key: "ended", label: "Past" },
];

const AuctionHouse = () => {
  const { format } = useCurrency();
  const [tab, setTab] = useState("live");
  const [auctions, setAuctions] = useState([]);

  useEffect(() => {
    api.get("/auctions", { params: { status: tab } }).then(({ data }) => setAuctions(data));
  }, [tab]);

  return (
    <div data-testid="auction-house-page" className="pt-32">
      <Seo
        title="Auction House · The Jakarta Sale · Live Gemstone Lots"
        description="OPIKJEWELS Auction House — live, upcoming, and past lots of investment-grade diamonds, Kashmir sapphires, Mogok rubies, Muzo emeralds, and Argyle pinks. Verified bidders only. Reserve prices observed."
        path="/auctions"
        jsonLd={breadcrumbSchema([
          { name: "Home", url: "/" },
          { name: "Auction House", url: "/auctions" },
        ])}
      />
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 mb-16">
        <p className="font-accent text-[11px] tracking-[0.28em] uppercase text-gold mb-5">Auction House</p>
        <h1 className="font-heading font-light text-5xl md:text-7xl leading-[1.05] max-w-4xl">The Jakarta Sale.</h1>
        <p className="mt-6 text-white/60 max-w-2xl leading-relaxed">Verified bidders only. Reserve prices observed. Proxy bidding supported. All lots accompanied by full provenance.</p>
      </section>

      <section className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16">
        <div className="flex gap-3 mb-12 border-b border-white/10 pb-6">
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`auction-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`font-accent text-[11px] tracking-[0.28em] uppercase px-5 py-3 transition-colors duration-500 ${
                tab === t.key ? "text-gold border-b border-gold -mb-[25px] pb-6" : "text-white/50 hover:text-white"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="space-y-6 pb-32">
          {auctions.map((a) => (
            <Link
              key={a.id}
              to={`/auctions/${a.id}`}
              data-testid={`auction-row-${a.id}`}
              className="grid grid-cols-1 md:grid-cols-[200px_1fr_auto] gap-6 md:gap-10 items-center bg-onyx border border-white/10 hover:border-gold/40 transition-all duration-500 p-6 group"
            >
              <div className="relative aspect-square bg-black overflow-hidden">
                <img src={a.gemstone?.image_url} alt={a.title} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000" />
                {a.gemstone?.video?.url && (
                  <VideoBadge className="absolute top-2 left-2 z-10" />
                )}
              </div>
              <div>
                <p className="font-accent text-[10px] tracking-[0.28em] uppercase text-gold mb-2">{a.gemstone?.category} · Lot</p>
                <h3 className="font-heading text-3xl md:text-4xl leading-tight">{a.title}</h3>
                <p className="text-white/50 text-sm mt-2">{a.gemstone?.name} · {a.gemstone?.carat} ct · {a.gemstone?.origin}</p>
                <div className="flex flex-wrap gap-8 mt-5">
                  <div>
                    <div className="font-accent text-[9px] tracking-[0.28em] uppercase text-white/40 mb-1">Current Bid</div>
                    <div className="font-heading text-2xl text-ivory">{format(a.current_bid)}</div>
                  </div>
                  <div>
                    <div className="font-accent text-[9px] tracking-[0.28em] uppercase text-white/40 mb-1">Bids</div>
                    <div className="font-heading text-2xl text-ivory">{a.bid_count}</div>
                  </div>
                </div>
              </div>
              <div className="md:text-right">
                {a.status !== "ended"
                  ? <CountdownTimer endsAt={a.ends_at} label={a.status === "live" ? "Closes In" : "Opens In"} />
                  : <div className="font-accent text-[11px] tracking-[0.28em] uppercase text-white/40">Auction Ended</div>}
              </div>
            </Link>
          ))}
          {auctions.length === 0 && <div className="text-white/40 text-center py-32">No {tab} auctions at this time.</div>}
        </div>
      </section>
    </div>
  );
};

export default AuctionHouse;
